import { ArrowBack, Delete, Edit } from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";

import TabPanel from "../../components/TabPanel";
import { getPerson } from "../../services/person";

import { Person } from "../../types/person";
import { AddNote } from "./AddNote";
import { DeletePerson } from "./DeletePerson";
import { EditPerson } from "./EditPerson";
import { Notes } from "./Notes";
import { Overview } from "./Overview";
import { useStyles } from "./styles";

export function DetailsPerson() {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeView, setActiveView] = useState<number>(0);
  const [dialogAddNote, setDialogAddNote] = useState<boolean>(false);
  const [dialogEditNote, setDialogEditNote] = useState<boolean>(false);
  const [dialogDeletePerson, setDialogDeletePerson] = useState<boolean>(false);

  useEffect(() => {
    if (!Boolean(location?.state)) {
      navigate("/");
    }
  }, [location?.state, navigate]);

  const personLocation: Person = useMemo(() => {
    return location?.state as Person;
  }, [location.state]);

  const person = useQuery(
    ["person", personLocation?.id_pessoa, personLocation?.documento],
    () => getPerson(personLocation)
  );

  const handleDialogAddNote = useCallback(() => {
    setDialogAddNote((prev) => !prev);
  }, []);

  const handleDialogEditNote = useCallback(() => {
    setDialogEditNote((prev) => !prev);
  }, []);

  const handleDialogDeletePerson = useCallback(() => {
    setDialogDeletePerson((prev) => !prev);
  }, []);

  const handleNavigateGoToBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleChangeView = useCallback((view: number) => {
    setActiveView(view);
  }, []);

  const RenderActions = useCallback(() => {
    if (activeView === 0) {
      return (
        <Stack direction="row" spacing={1}>
          <Button
            onClick={handleDialogEditNote}
            startIcon={<Edit />}
            variant="outlined"
          >
            Editar
          </Button>
          <Button
            onClick={handleDialogDeletePerson}
            startIcon={<Delete />}
            color="error"
            variant="outlined"
          >
            Excluir
          </Button>
        </Stack>
      );
    } else {
      return (
        <Button onClick={handleDialogAddNote} variant="contained">
          Adicionar nota
        </Button>
      );
    }
  }, [
    activeView,
    handleDialogAddNote,
    handleDialogDeletePerson,
    handleDialogEditNote,
  ]);

  return (
    <>
      <Stack spacing={4} className={styles.root}>
        <Stack alignItems="flex-start" direction="row" spacing={1}>
          <Tooltip placement="top" title="Voltar">
            <IconButton onClick={handleNavigateGoToBack}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Stack spacing={2}>
            <Typography variant="h4">
              {person.data?.nome || "Cliente não identificado"}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => handleChangeView(0)}
              variant={activeView === 0 ? "contained" : "outlined"}
            >
              Visão geral
            </Button>
            <Button
              onClick={() => handleChangeView(1)}
              variant={activeView === 1 ? "contained" : "outlined"}
            >
              Notas
            </Button>
          </Stack>
          {RenderActions()}
        </Stack>

        <TabPanel value={activeView} index={0}>
          <Overview loading={person.isLoading} person={person.data} />
        </TabPanel>
        <TabPanel value={activeView} index={1}>
          <Notes person={person.data} />
        </TabPanel>
      </Stack>
      <AddNote
        person={person.data}
        open={dialogAddNote}
        onClose={handleDialogAddNote}
      />
      <EditPerson
        person={person.data}
        open={dialogEditNote}
        onClose={handleDialogEditNote}
      />
      <DeletePerson
        person={person.data}
        open={dialogDeletePerson}
        onClose={handleDialogDeletePerson}
        onGoBack={handleNavigateGoToBack}
      />
    </>
  );
}
